import { useEffect, useRef, useState } from 'react';

const useActualNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [isChecking, setIsChecking] = useState(false);
  const probeInFlight = useRef(false);

  const testConnectivity = async () => {
    if (probeInFlight.current) return;
    probeInFlight.current = true;
    setIsChecking(true);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      await fetch('/api/health-check', {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-store',
      });
      clearTimeout(timeoutId);
      setIsOnline(true);
    } catch {
      clearTimeout(timeoutId);
      // Probe failed — trust the browser's own network signal
      setIsOnline(navigator.onLine);
    } finally {
      setIsChecking(false);
      probeInFlight.current = false;
    }
  };

  useEffect(() => {
    setIsOnline(navigator.onLine);

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Probe every 30s to catch captive portals / silent disconnects
    const intervalId = setInterval(() => {
      if (navigator.onLine) testConnectivity();
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(intervalId);
    };
  }, []);

  return { isOnline, isChecking };
};

export default useActualNetworkStatus;
